<app-popup-reusable
  [isOpen]="isOpen()"
  size="full"
  maxHeightClass="h-[92dvh]"
  bodyPaddingClass="p-0"
  [showDefaultFooter]="false"
  [showCloseButton]="false"
  [closeOnBackdrop]="true"
  (close)="onClose()"
  >
  <div class="flex h-full min-h-0 flex-col" (contextmenu)="onContextMenu($event)">
    <div class="flex shrink-0 items-center justify-between gap-4 border-b border-slate200 px-5 py-3">
      <div class="min-w-0 flex-1">
        <p class="truncate text-sm font-semibold text-slate900">{{ fileName() }}</p>
      </div>

      @if (canZoom()) {
        <div class="flex shrink-0 items-center gap-1 rounded-lg border border-slate200 bg-white p-1">
          <button
            type="button"
            aria-label="Zoom out"
            title="Zoom out (Ctrl -)"
            class="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-slate800 hover:bg-slate100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            [disabled]="!canZoomOut()"
            (click)="zoomOut()"
            >
            <svg viewBox="0 0 20 20" class="h-4 w-4 fill-none stroke-current" stroke-width="2">
              <path d="M5 10h10"></path>
            </svg>
          </button>

          <button
            type="button"
            aria-label="Reset zoom to 100%"
            title="Actual size (Ctrl 0)"
            class="h-7 min-w-14 cursor-pointer rounded-md px-1 text-xs font-medium tabular-nums text-slate800 hover:bg-slate100"
            (click)="resetZoom()"
            >
            {{ zoomPercent() }}%
          </button>

          <button
            type="button"
            aria-label="Zoom in"
            title="Zoom in (Ctrl +)"
            class="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-slate800 hover:bg-slate100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            [disabled]="!canZoomIn()"
            (click)="zoomIn()"
            >
            <svg viewBox="0 0 20 20" class="h-4 w-4 fill-none stroke-current" stroke-width="2">
              <path d="M5 10h10"></path>
              <path d="M10 5v10"></path>
            </svg>
          </button>

          <span class="mx-1 h-5 w-px bg-slate200"></span>

          <button
            type="button"
            aria-label="Fit to width"
            title="Fit to width"
            class="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-slate800 hover:bg-slate100"
            (click)="fitToWidth()"
            >
            <svg viewBox="0 0 20 20" class="h-4 w-4 fill-none stroke-current" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2.5 10h15"></path>
              <path d="M5 7.5 2.5 10 5 12.5"></path>
              <path d="M15 7.5l2.5 2.5-2.5 2.5"></path>
            </svg>
          </button>
        </div>
      }

      <button
        type="button"
        aria-label="Close document viewer"
        class="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-slate800 hover:bg-slate100"
        (click)="onClose()"
        >
        <svg viewBox="0 0 20 20" class="h-5 w-5 fill-none stroke-current" stroke-width="2">
          <path d="M5 5l10 10"></path>
          <path d="M15 5 5 15"></path>
        </svg>
      </button>
    </div>

    <!-- min-h keeps the frame visible even if the flex chain above it collapses. -->
    <div #stage class="viewer-stage relative min-h-[60vh] flex-1 bg-slate100">
      @switch (viewMode()) {
        @case ('loading') {
          <div class="flex h-full items-center justify-center">
            <div class="flex flex-col items-center gap-3">
              <span class="h-8 w-8 animate-spin rounded-full border-2 border-slate300 border-t-slate600"></span>
              <p class="text-xs text-slate500">Loading document…</p>
            </div>
          </div>
        }
        @case ('unsupported') {
          <div class="flex h-full items-center justify-center px-6 text-center">
            <div>
              <p class="text-sm font-semibold text-slate900">Preview not available</p>
              <p class="mt-1 text-xs text-slate500">
                This file type can't be displayed in the browser.
              </p>
            </div>
          </div>
        }
        @case ('pdf') {
          <!-- Each page is an A4-wide sheet; the canvas inside is drawn by pdf.js. -->
          <div #scroller class="absolute inset-0 overflow-auto">
            <div class="relative flex w-max min-w-full flex-col items-center gap-4 p-6">
              @for (page of pages(); track page.index) {
                <div
                  #pageEl
                  class="viewer-sheet shrink-0 bg-white"
                  [attr.data-page]="page.index"
                  [style.width.px]="page.width * zoom()"
                  [style.height.px]="page.height * zoom()"
                ></div>
              }
            </div>
          </div>
        }
        @case ('image') {
          <div #scroller class="absolute inset-0 overflow-auto">
            <div class="flex w-max min-w-full justify-center p-6">
              <div
                class="viewer-sheet flex shrink-0 items-start justify-center bg-white"
                [style.width.px]="a4Width * zoom()"
                [style.height.px]="a4Height * zoom()"
                [style.padding.px]="24 * zoom()"
                >
                <img
                  [src]="resolvedUrl()"
                  [alt]="fileName()"
                  draggable="false"
                  class="max-h-full max-w-full object-contain"
                  />
              </div>
            </div>
          </div>
        }
        @case ('pdf-frame') {
          <iframe
            [src]="frameUrl()"
            [title]="fileName()"
            class="absolute inset-0 h-full w-full border-none"
          ></iframe>
        }
        @default {
          <div class="flex h-full items-center justify-center px-6 text-center">
            <div>
              <p class="text-sm font-semibold text-slate900">Couldn't load this document</p>
              <p class="mt-1 text-xs text-slate500">Please close this window and try again.</p>
            </div>
          </div>
        }
      }
    </div>
  </div>
</app-popup-reusable>
